import { detectLocale, type Locale } from '@agnivo/app-core';

/**
 * ONE locale decision for this surface, frozen at boot.
 *
 * ## Why a frozen value and not a fresh `detectLocale()` per render
 *
 * The mini-app's locale signal is MUTABLE. `detectLocale()` prefers Telegram
 * `initDataUnsafe.user.language_code` over `navigator.language`, the Telegram SDK tag is `async`,
 * and `bootstrapTelegram()` is deliberately not awaited — so the same function returns different
 * answers before and after the handshake lands.
 *
 * ⛔ **That is what made the previous shape wrong, and I wrote it in this MR.** `main.tsx` applied
 * `<html lang>` once at boot; `LaunchGate` resolved its dictionary on EVERY render; and
 * `useTelegramSdkPresent()` exists precisely to re-render when the SDK arrives late. So: an EN
 * phone with the SDK absent at boot froze `lang="en"`, then the SDK landed carrying
 * `language_code="ru"`, the hook re-rendered, and the launch copy flipped to Russian underneath
 * an English `lang`. Product caught it at `0c4ce89` (`!509`). The comment in `main.tsx` had argued
 * the re-apply was unnecessary because rendered copy was fixed at module scope — true when that
 * comment was written, and falsified by my own change in the same MR that made the launch copy
 * dictionary-driven. The argument survived the fact it depended on.
 *
 * Freezing closes it by removing the second reading: there is no later locale for the copy to move
 * to, so the attribute and the copy cannot diverge no matter when the SDK arrives.
 *
 * ⚠️ **This deliberately does NOT fix the underlying race**, and must not be read as having done
 * so. When the SDK is late, the frozen value comes from `navigator.language` — the PHONE's
 * language, not the Telegram user's — so a Russian user on an English phone gets an English
 * mini-app for that session. That is a real, pre-existing product defect tracked on its own card
 * (`2026-08-30-frontend-mini-app-renders-in-the-phones-language-when-the-telegram-sdk-is-late`).
 * Freezing makes it STABLE rather than correct, which is strictly better than the flip it replaces
 * — a wrong-but-consistent language is a bug; a language that changes mid-session while `lang`
 * stays put is that same bug plus a broken accessibility contract. The real fix is to establish
 * the locale before first paint, which is that card's business, not this MR's.
 */
let frozen: Locale | null = null;

/**
 * Record the boot locale. Idempotent BY CONTRACT, not by convention: a second call — from a late
 * handshake, a re-import under HMR, a re-mount in a test — is a no-op that returns the first
 * answer. That idempotence is the entire mechanism, so it is asserted directly in
 * `test/launchLocaleAgreement.test.tsx` rather than left as a property of the one call site.
 */
export function freezeBootLocale(locale: Locale): Locale {
  frozen ??= locale;
  return frozen;
}

/**
 * The locale every rendered string on this surface must use.
 *
 * If boot never ran — a component test importing a screen directly, or any render outside
 * `main.tsx` — this resolves one and freezes it. Falling back to a locale LITERAL instead would
 * put a second locale decision in the codebase, which is the exact class this MR exists to remove.
 *
 * ⛔ It resolves with `detectLocale()` and deliberately does NOT call `applyDocumentLocale()`.
 * Reading a locale and writing `<html lang>` are different jobs, and only boot may do the second:
 * `applyDocumentLocale()` touches `document`, and this module is imported on render paths where
 * there is no DOM at all. That is not hypothetical — `test/launchGate.test.tsx` renders
 * `LaunchGate` under vitest's **node** environment, and the first version of this function threw
 * `ReferenceError: document is not defined` across seven cases there. A DOM write hidden inside a
 * getter also fires at whatever arbitrary moment the first render happens, which is precisely the
 * "attribute moves on its own schedule" shape this file was written to eliminate.
 *
 * `detectLocale()` is the same resolver `applyDocumentLocale()` writes from, so when boot HAS run
 * the frozen value is already set and this returns it unchanged — the two can never disagree.
 */
export function getBootLocale(): Locale {
  return (frozen ??= detectLocale());
}
